"use client";

import { authStore } from "@kaplan-labs/up-auth-api-client";
import { useStore } from "@nanostores/react";
import { HTTPError } from "ky";

import { themeStore } from "./library-theme-context";
import { logError } from "./log-error";
import { persistentAtom } from "./persistent-store";
import { ProfileType } from "./types";

import type { TContact, TContactResponse } from "./types/contact";
import type { TFirstTimeUser, TProfile } from "./types/profile";
import type { Result } from "./types/result";

export const SESSION_PROFILE_KEY = "__upprofile";
export const UP_PROFILE_DID_CHANGE = "up_profile_did_change";

export type TProfileContext = {
  isLoading: boolean;
  didError: boolean;
  data?: TProfile | TFirstTimeUser;
  contact?: TContact;
  errorCode?: number | undefined;
};

export const profileStore = persistentAtom<TProfileContext>(
  SESSION_PROFILE_KEY,
  {
    isLoading: false,
    didError: false,
    data: undefined,
  },
  {
    encode: JSON.stringify,
    decode: JSON.parse,
  },
);

const _emitEvent = (eventName: string, payload: unknown) => {
  const event = new CustomEvent(eventName, {
    detail: payload,
  });

  document.dispatchEvent(event);
};

const _getProfile = async function (
  baseURL: string,
  partnerID: string,
): Promise<Result<TContactResponse | null, HTTPError>> {
  const $auth = authStore.get();

  try {
    const { data } = await $auth.client
      .get(`${baseURL}/v3/contacts/me?partnerId=${partnerID}`)
      .json<{ data: TContactResponse }>();

    return {
      value: data,
      error: null,
    };
  } catch (_err: unknown) {
    const error = _err as HTTPError;
    if (error.response?.status === 404) {
      //This means it's a fresh user - no profile for this partner
      return Promise.resolve({ value: null, error: null });
    }
    return Promise.reject(error);
  }
};

/*
  When calling /v3/contacts/me?partnerId= the response contains an
  array of applications, scholarships, and profiles.
  There should only be one of each.
*/
const adaptForPartner = (fullContact: TContactResponse): TContact<TProfile> => {
  const { profiles, applications, scholarships, ...restContact } = fullContact;
  return {
    profile: profiles[0],
    application: applications?.[0],
    scholarship: scholarships?.[0],
    ...restContact,
  };
};

export const _fetchCurrentUserProfile = async (
  baseURL: string,
  partnerID: string,
): Promise<
  Result<
    {
      contact: TContact;
      profile: TProfile | TFirstTimeUser;
    },
    Error
  >
> => {
  const $auth = authStore.get();

  try {
    const resp: Result<TContactResponse | null, HTTPError> = await _getProfile(
      baseURL,
      partnerID,
    );

    if (resp.error !== null) {
      return { value: null, error: resp.error };
    }
    if (resp.value === null) {
      //first time user
      if (!$auth.currentUser?.contactId) {
        return { value: null, error: new Error("No contactId for user") };
      }
      const profile = {
        contactId: $auth.currentUser.contactId,
        email: $auth.currentUser.email,
      };
      return {
        value: {
          contact: { _id: $auth.currentUser.contactId, profile },
          profile,
        },
        error: null,
      };
    }
    const fullContact = resp.value;
    const contact = adaptForPartner(fullContact);
    const profile = contact.profile;

    if (!("type" in profile) || profile.type === ProfileType.Student) {
      return { value: { contact, profile }, error: null };
    }
    const { data: relationships } = await $auth.client
      .get(
        `${baseURL}/v3/contacts/${fullContact._id}/related?partnerId=${partnerID}`,
      )
      .json<{ data: TContactResponse[] }>();

    return {
      value: {
        contact,
        profile: {
          ...(profile as TProfile),
          relatedStudents: relationships.map(adaptForPartner),
        },
      },
      error: null,
    };
  } catch (err) {
    return { value: null, error: err as HTTPError };
  }
};

export const sync = async () => {
  const BASE_URL = process.env["NEXT_PUBLIC_HALL_OF_RECORDS_URI"] as string;
  const $auth = authStore.get();
  const $theme = themeStore.get();
  const $profile = profileStore.get();

  if (!$auth.currentUser || !$theme.partnerID) {
    profileStore.set({
      isLoading: false,
      didError: false,
      data: undefined,
    });
    return;
  }

  profileStore.set({
    ...$profile,
    isLoading: true,
  });
  const { value, error } = await _fetchCurrentUserProfile(
    BASE_URL,
    $theme.partnerID,
  );

  if (error) {
    profileStore.set({
      errorCode: error instanceof HTTPError ? error.response.status : 500,
      isLoading: false,
      didError: true,
      data: undefined,
    });
    logError({ action: "PROFILE_LOAD", error, context: { auth: $auth } });
    _emitEvent(UP_PROFILE_DID_CHANGE, { profile: undefined });
  } else {
    profileStore.set({
      isLoading: false,
      didError: false,
      data: value.profile,
      contact: value.contact,
    });
    _emitEvent(UP_PROFILE_DID_CHANGE, { profile: value.profile });
  }
};

export const profileInit = function () {
  authStore.listen(sync);
  themeStore.listen(sync);
  sync();
};

/*
  React hook to force renders when profile changes
*/
export const useProfile = function () {
  return useStore(profileStore);
};
